import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAlert } from '../../context/AlertContext';
import { useGetCountries } from '../../hooks/reactQuerys/CountryQueries';
import type { State } from '../../redux/mainReducer';
import {
  IPostcode,
  useCreatePostcode,
  useUpdatePostcode,
} from '../../hooks/reactQuerys/PostcodeAdminQueries';

export type PostcodeEditMode = 'create' | 'edit' | 'copy';

interface IProps {
  open: boolean;
  mode: PostcodeEditMode;
  initial?: IPostcode | null;
  onClose: () => void;
  onSaved: () => void;
}

const backendError = (err: unknown): string | undefined =>
  (err as { body?: { error?: string } })?.body?.error;

/**
 * Anlegen / Bearbeiten / Kopieren einer Postleitzahl. „Kopieren" öffnet den
 * Dialog mit den Werten der Vorlage, speichert aber als neuen Eintrag.
 * Die Dublettenprüfung erfolgt serverseitig (409 → Meldung).
 */
export const PostcodeEditDialog = ({
  open,
  mode,
  initial,
  onClose,
  onSaved,
}: IProps) => {
  const { showAlert } = useAlert();
  const { createPostcode, isCreatingPostcode } = useCreatePostcode();
  const { updatePostcode, isUpdatingPostcode } = useUpdatePostcode();

  const { countries } = useGetCountries();
  const federalStates = useSelector(
    (state: State) => state.federalStateArray.storeFederalState
  );

  // Deutschland als Vorauswahl für neue PLZ (per Name, kein hartkodiertes id).
  const germanCountryId = useMemo(
    () =>
      countries?.find(c => /deutschland|germany/i.test(c.Country ?? ''))
        ?.idCountry,
    [countries]
  );

  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [countryId, setCountryId] = useState<number | ''>('');
  const [federalStateId, setFederalStateId] = useState<number | ''>('');

  useEffect(() => {
    if (open) {
      setPostcode(initial?.Postcode ?? '');
      setCity(initial?.City ?? '');
      setCountryId(initial?.Country_idCountry ?? germanCountryId ?? '');
      setFederalStateId(initial?.FederalState_idFederalState ?? '');
    }
  }, [open, initial, germanCountryId]);

  // Länderliste kann nach dem Öffnen nachladen — dann Deutschland für neue
  // Einträge nachträglich vorbelegen (nur wenn noch kein Land gewählt ist).
  useEffect(() => {
    if (open && countryId === '' && !initial?.Country_idCountry && germanCountryId) {
      setCountryId(germanCountryId);
    }
  }, [open, countryId, germanCountryId, initial]);

  const saving = isCreatingPostcode || isUpdatingPostcode;

  const title =
    mode === 'edit'
      ? 'Postleitzahl bearbeiten'
      : mode === 'copy'
        ? 'Postleitzahl kopieren'
        : 'Neue Postleitzahl';

  const handleSave = async () => {
    if (!postcode.trim() || !city.trim()) {
      showAlert({
        text: 'Bitte Postleitzahl und Ort angeben.',
        severity: 'warning',
      });
      return;
    }
    const body: Partial<IPostcode> = {
      Postcode: postcode.trim(),
      City: city.trim(),
      City_idCity: mode === 'edit' ? initial?.City_idCity : null,
      // Bundesland optional (leer = keins, z. B. für ausländische PLZ).
      FederalState_idFederalState:
        federalStateId === '' ? null : Number(federalStateId),
      // Land: bei leer Fallback auf bestehenden Wert; Backend defaultet sonst.
      Country_idCountry:
        countryId === ''
          ? (initial?.Country_idCountry ?? null)
          : Number(countryId),
    };
    try {
      if (mode === 'edit' && initial) {
        await updatePostcode({ idPostcode: initial.idPostcode, body });
        showAlert({ text: 'Postleitzahl gespeichert.', severity: 'success' });
      } else {
        await createPostcode(body);
        showAlert({ text: 'Postleitzahl angelegt.', severity: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      showAlert({
        text: backendError(err) ?? 'Speichern fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label='Postleitzahl'
            value={postcode}
            onChange={e => setPostcode(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label='Ort'
            value={city}
            onChange={e => setCity(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label='Land'
            value={countryId}
            onChange={e =>
              setCountryId(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            fullWidth
          >
            {(countries ?? []).map(c => (
              <MenuItem key={c.idCountry} value={c.idCountry}>
                {c.Country}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label='Bundesland (optional)'
            value={federalStateId}
            onChange={e =>
              setFederalStateId(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            fullWidth
          >
            <MenuItem value=''>
              <em>— keins —</em>
            </MenuItem>
            {(federalStates ?? []).map(fs => (
              <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                {fs.FederalState}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
